/**
 * GroupsPage Component
 *
 * Main page for managing groups with search, filtering, and CRUD operations.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { GroupTable } from '../components/GroupTable';
import { CreateGroupModal } from '../components/CreateGroupModal';
import { GroupDetailsModal } from '../components/GroupDetailsModal';
import { UpdateGroupModal } from '../components/UpdateGroupModal';
import { ManageMembersModal } from '../components/ManageMembersModal';
import { DeleteGroupModal } from '../components/DeleteGroupModal';
import { useGroups } from '../hooks/useGroups';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { GroupSanitized } from '../../../types/group.types';
import { getErrorMessage } from '../../../lib/utils/errors';

export const GroupsPage: React.FC = () => {
  const { user, tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showMembersModal, setShowMembersModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'iam:CreateGroup');
  const canUpdate = hasPermission(activeRole?.permissions, 'iam:UpdateGroup');
  const canDelete = hasPermission(activeRole?.permissions, 'iam:DeleteGroup');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch groups
  const { data: groups = [], isLoading, isFetching, error, refetch } = useGroups(tenantId);

  // Filter groups
  const filteredGroups = useMemo(() => {
    if (!groups) return [];

    let filtered = [...groups];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (group) =>
          group.groupName.toLowerCase().includes(query) ||
          (group.description && group.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [groups, debouncedSearch]);

  const handleViewDetails = (group: GroupSanitized) => {
    setSelectedGroup(group);
    setShowDetailsModal(true);
  };

  const handleEdit = (group: GroupSanitized) => {
    setSelectedGroup(group);
    setShowUpdateModal(true);
  };

  const handleManageMembers = (group: GroupSanitized) => {
    setSelectedGroup(group);
    setShowMembersModal(true);
  };

  const handleDelete = (group: GroupSanitized) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Groups</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Organize users into groups for easier role and permission management
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateModal(true)}>
              Create Group
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Groups Table */}
        <GroupTable
          groups={filteredGroups}
          isLoading={isLoading}
          onViewGroup={handleViewDetails}
          onEditGroup={handleEdit}
          onDeleteGroup={handleDelete}
          onManageMembers={handleManageMembers}
          canEdit={canUpdate}
          canDelete={canDelete}
        />

        {/* Modals */}
        <CreateGroupModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => refetch()}
        />

        <GroupDetailsModal
          group={selectedGroup}
          tenantId={tenantId}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedGroup(null);
          }}
        />

        <UpdateGroupModal
          group={selectedGroup}
          tenantId={tenantId}
          isOpen={showUpdateModal}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedGroup(null);
          }}
          onSuccess={() => refetch()}
        />

        <ManageMembersModal
          group={selectedGroup}
          tenantId={tenantId}
          isOpen={showMembersModal}
          onClose={() => {
            setShowMembersModal(false);
            setSelectedGroup(null);
          }}
        />

        {selectedGroup && (
          <DeleteGroupModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedGroup(null);
            }}
            group={selectedGroup}
            onSuccess={() => refetch()}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
